import React, { useState } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Context
import { useI18n } from '../context/I18nContext';

// Data
import { citiesData } from '../data/citiesData';

const PageContainer = styled(Container)`
  padding-top: 2rem;
  padding-bottom: 4rem;
`;

const CityCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const CityImage = styled(CardMedia)`
  height: 200px;
`;

const AudioControls = styled(Box)`
  margin-top: 16px;
`;

const AudioButton = styled(Button)`
  margin: 0 4px;
`;

const RegionTab = styled(Tab)`
  text-transform: none;
  font-weight: 500;
`;

const CitiesPage: React.FC = () => {
  const { t, language } = useI18n();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState<any | null>(null);
  const [currentAudioLang, setCurrentAudioLang] = useState<'pt' | 'en' | 'es'>('pt');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };
  
  const handleCityClick = (city: any) => {
    setSelectedCity(city);
    setCurrentAudioLang(language);
  };
  
  const handleCloseModal = () => {
    setSelectedCity(null);
  };
  
  const handleAudioLanguageChange = (lang: 'pt' | 'en' | 'es') => {
    setCurrentAudioLang(lang);
  };
  
  const handleRegionChange = (event: React.SyntheticEvent, newValue: string) => {
    setRegionFilter(newValue);
  };
  
  const filteredCities = citiesData.filter(city => {
    const matchesSearch = city.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                        city.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = regionFilter === 'all' || city.region === regionFilter;
    
    return matchesSearch && matchesRegion;
  });
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <PageContainer maxWidth="lg">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h2" component="h1" align="center" gutterBottom color="primary">
            {t('cities_title')}
          </Typography>
          
          <Typography variant="h6" align="center" paragraph color="textSecondary">
            {t('cities_subtitle')}
          </Typography>
          
          <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'space-between' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar cidades..."
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ mb: { xs: 2, md: 0 }, maxWidth: { md: '350px' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            <Tabs
              value={regionFilter}
              onChange={handleRegionChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ '& .MuiTabs-indicator': { backgroundColor: 'primary.main' } }}
            >
              <RegionTab value="all" label="Todas as Regiões" />
              <RegionTab value="norte" label="Norte" />
              <RegionTab value="nordeste" label="Nordeste" />
              <RegionTab value="centro-oeste" label="Centro-Oeste" />
              <RegionTab value="sudeste" label="Sudeste" />
              <RegionTab value="sul" label="Sul" />
            </Tabs>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', margin: -1.5 }}>
            {filteredCities.length > 0 ? (
              filteredCities.map((city) => (
                <Box key={city.id} sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' }, padding: 1.5 }}>
                  <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <CityCard onClick={() => handleCityClick(city)}>
                      <CityImage
                        image={city.image}
                        title={city.name}
                      />
                      <CardContent sx={{ p: '16px', pb: '8px', flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative', height: '200px' }}>
                        <Typography variant="h6" component="h3" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                          {city.name}
                        </Typography>
                        
                        <Typography variant="body2" color="textSecondary" sx={{ 
                          mb: 5,
                          mt: 1, 
                          height: '80px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {city.description}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'absolute', bottom: 13, left: 16, right: 16 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              component="span"
                              sx={{
                                bgcolor: 'rgba(0, 77, 64, 0.12)',
                                color: 'rgba(0, 77, 64, 0.9)',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                px: 1,
                                py: 0.4,
                                borderRadius: '4px',
                                display: 'inline-flex',
                                alignItems: 'center'
                              }}
                            >
                              <LocationOnIcon sx={{ fontSize: '0.75rem', mr: 0.5 }} />
                              {city.state}
                            </Box>
                            <Box 
                              component="span"
                              sx={{ 
                                bgcolor: 'rgba(97, 97, 97, 0.08)',
                                color: 'rgba(97, 97, 97, 0.9)',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                px: 1,
                                py: 0.4,
                                borderRadius: '4px',
                                display: 'inline-flex',
                                alignItems: 'center'
                              }}
                            >
                              {city.region === 'centro-oeste' ? 'Centro-Oeste' : city.region}
                            </Box>
                          </Box>
                          
                          <Button size="small" color="primary" sx={{ textTransform: 'none' }}>
                            Saiba mais →
                          </Button>
                        </Box>
                      </CardContent>
                    </CityCard>
                  </motion.div>
                </Box>
              ))
            ) : (
              <Box sx={{ width: '100%', textAlign: 'center', py: 5 }}>
                <Typography variant="h6" color="text.secondary">
                  Nenhuma cidade encontrada com os critérios de busca atuais.
                </Typography>
              </Box>
            )}
          </Box>
        </motion.div>
        
        {/* Modal de detalhes da cidade */}
        <Dialog
          open={Boolean(selectedCity)}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
        >
          {selectedCity && (
            <>
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" component="div">
                  {selectedCity.name}
                </Typography>
                <IconButton edge="end" color="inherit" onClick={handleCloseModal} aria-label="close">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              
              <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                  <img
                    src={selectedCity.image}
                    alt={selectedCity.name}
                    style={{ width: '100%', borderRadius: '8px', maxHeight: '400px', objectFit: 'cover' }}
                  />
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Box
                    component="span"
                    sx={{
                      bgcolor: 'rgba(0, 77, 64, 0.12)',
                      color: 'rgba(0, 77, 64, 0.9)',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '4px',
                      display: 'inline-flex',
                      alignItems: 'center'
                    }}
                  >
                    <LocationOnIcon sx={{ fontSize: '0.85rem', mr: 0.5 }} />
                    {selectedCity.state}
                  </Box>
                  <Box 
                    component="span"
                    sx={{ 
                      bgcolor: 'rgba(97, 97, 97, 0.08)',
                      color: 'rgba(97, 97, 97, 0.9)',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '4px',
                      display: 'inline-flex',
                      alignItems: 'center'
                    }}
                  >
                    {selectedCity.region === 'centro-oeste' ? 'Centro-Oeste' : selectedCity.region}
                  </Box>
                </Box>
                
                <Typography variant="h6" gutterBottom color="primary">
                  {t('modal_history')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {selectedCity.history}
                </Typography>
                
                <Typography variant="h6" gutterBottom color="primary">
                  {t('modal_culture')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {selectedCity.culture}
                </Typography>
                
                <Typography variant="h6" gutterBottom color="primary">
                  {t('modal_attractions')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {selectedCity.attractions}
                </Typography>
                
                {selectedCity.hasAudio && (
                  <Box mt={3}>
                    <Typography variant="h6" gutterBottom color="primary">
                      {t('modal_listen')}
                    </Typography>
                    
                    <AudioControls>
                      <Box sx={{ mb: 2 }}>
                        <AudioButton
                          variant={currentAudioLang === 'pt' ? 'contained' : 'outlined'}
                          color="primary"
                          onClick={() => handleAudioLanguageChange('pt')}
                        >
                          {t('audio_pt')}
                        </AudioButton>
                        <AudioButton
                          variant={currentAudioLang === 'en' ? 'contained' : 'outlined'}
                          color="primary"
                          onClick={() => handleAudioLanguageChange('en')}
                        >
                          {t('audio_en')}
                        </AudioButton>
                        <AudioButton
                          variant={currentAudioLang === 'es' ? 'contained' : 'outlined'}
                          color="primary"
                          onClick={() => handleAudioLanguageChange('es')}
                        >
                          {t('audio_es')}
                        </AudioButton>
                      </Box>
                      
                      <audio
                        controls
                        src={`/assets/audio/${selectedCity.id}_${currentAudioLang}.mp3`}
                        style={{ width: '100%' }}
                      >
                        Seu navegador não suporta o elemento de áudio.
                      </audio>
                    </AudioControls>
                  </Box>
                )}
              </DialogContent>
              
              <DialogActions>
                <Button onClick={handleCloseModal} color="primary">
                  Fechar
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </PageContainer>
    </motion.div>
  );
};

export default CitiesPage;
